import type {NonEmptyString, UniqueNonEmptyArray} from '@pexip/utils/nonEmpty';
import {
    isNonEmptyString,
    isUniqueNonEmptyArrayOfNonEmptyString,
} from '@pexip/utils/nonEmpty';

/** An independently owned, immutable static redaction policy. */
export interface RedactionPolicy {
    /** Text replacing values under sensitive keys. */
    readonly replacement: NonEmptyString;
    /** Unique sensitive keys matched exactly and case-sensitively. */
    readonly keys: UniqueNonEmptyArray<NonEmptyString>;
}

/**
 * Parses decoded policy data into an immutable, independently owned policy.
 *
 * @throws When the policy does not match the required schema.
 * @example
 * parsePolicy({replacement: '[REDACTED]', keys: ['token']});
 */
export const parsePolicy = (input: unknown): RedactionPolicy => {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
        throw new Error('RedactionPolicy: invalid root');
    }

    const fields = Object.keys(input);

    if (
        fields.length !== 2 ||
        fields.some(key => key !== 'replacement' && key !== 'keys') ||
        !('replacement' in input) ||
        !('keys' in input)
    ) {
        throw new Error('RedactionPolicy: invalid fields');
    }

    const replacement = input.replacement;
    const rawKeys = input.keys;

    if (!isNonEmptyString(replacement) || !Array.isArray(rawKeys)) {
        throw new Error('RedactionPolicy: invalid values');
    }

    const keys: readonly unknown[] = Object.freeze([...rawKeys]);

    if (!isUniqueNonEmptyArrayOfNonEmptyString(keys)) {
        throw new Error('RedactionPolicy: invalid keys');
    }

    return Object.freeze({replacement, keys});
};
